<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useInterval, useScroll, watchThrottled } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { useChapterTheme } from './composables/useChapterTheme'
import { useChapterLoader } from './composables/useChapterLoader'
import { useChapterTracking } from './composables/useChapterTracking'
import { useChapterScroll } from './composables/useChapterScroll'
import ChapterUnlockDefault from './components/ChapterUnlock.vue'
import ChapterThemeSettingsDefault from './components/ChapterThemeSettings.vue'
import ChapterNavigation from './components/ChapterNavigation.vue'
import ChapterLeadSentence from './components/ChapterLeadSentence.vue'
import ChapterRecommend from './components/ChapterRecommend.vue'
import ReaderTaboolaGuide from './components/ReaderTaboolaGuide.vue'
import ChapterBreadcrumb from './components/ChapterBreadcrumb.vue'
import BizPopupBookTOCDefault from '@/components/BizPopupBookTOC.vue'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import { CoinsSuccessPopup as ChapterCoinsSuccess, getChapterConfig } from '@/composables/payment'
import WelfareGuideOpen from '@/pages/welfare/components/modules/WelfareGuideOpen.vue'
import { $formatErrorMessage } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, ERROR_PAGE_LEVEL, VIP_TYPE } from '@/constants'
import { syncChapterListWithAudioStore, useAudioPlayerStore, useToastStore, useUserStore } from '@/stores'
import { useBookshelf } from '@/composables/useBookshelf'
import { bannerShow, useBannerShow, useCallRocnovelApp } from '@/composables/useBannerShow'
import { isInThirdAppBrowser } from '@/utils/bowserEnv'
import { pageViewTrack, useBookRead, useChapterReadComplete, useElementClick } from '@/composables/tracks'
import { useWheelOrSwipeUpListener } from '@/composables/useWheelOrSwipeUpListener'
import { brand, brandConfig, features } from '@/config/brands'
import ScrollChapterAdGpt from '@/pages/chapter-scroll/components/ScrollChapterAdGpt.vue'
import { useGptAdManager } from '@/pages/chapter-scroll/composables/useGptAdManager'
import { createDebugger } from '@/utils/debug'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { useFbqReadChapter } from '@/composables/fbq/useFbqReadChapter'
import { useFbqViewContent } from '@/composables/fbq/useFbqViewContent'
import { useFbqReadDuration } from '@/composables/fbq/useFbqReadDuration'
import { useReaderTaboolaGuide } from '@/pages/chapter/composables/useReaderTaboolaGuide'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()

// 加载当前品牌配置
const config = getChapterConfig()

// 加载品牌专属主题设置弹窗
const ChapterThemeSettings = loadBrandComponent({
  rocnovel: ChapterThemeSettingsDefault,
  oceread: ChapterThemeSettingsDefault,
  ficmoon: ChapterThemeSettingsDefault,
  novelarc: ChapterThemeSettingsDefault,
  storyella: ChapterThemeSettingsDefault,
  silktale: ChapterThemeSettingsDefault,
  pearla: ChapterThemeSettingsDefault,
  fictora: ChapterThemeSettingsDefault,
  marinovel: ChapterThemeSettingsDefault,
})
const ChapterUnlock = loadBrandComponent({
  rocnovel: ChapterUnlockDefault,
  oceread: ChapterUnlockDefault,
  ficmoon: ChapterUnlockDefault,
  novelarc: ChapterUnlockDefault,
  storyella: ChapterUnlockDefault,
  silktale: ChapterUnlockDefault,
  pearla: ChapterUnlockDefault,
  fictora: ChapterUnlockDefault,
  marinovel: ChapterUnlockDefault,
})
const ChapterTOCPopup = loadBrandComponent({
  rocnovel: BizPopupBookTOCDefault,
  oceread: BizPopupBookTOCDefault,
  ficmoon: BizPopupBookTOCDefault,
  novelarc: BizPopupBookTOCDefault,
  storyella: BizPopupBookTOCDefault,
  silktale: BizPopupBookTOCDefault,
  pearla: BizPopupBookTOCDefault,
  fictora: BizPopupBookTOCDefault,
  marinovel: BizPopupBookTOCDefault,
  flipnovel: BizPopupBookTOCFlipnovel,
  storlyn: BizPopupBookTOCFlipnovel,
})

const route = useRoute()
const router = useRouter()
const { showReaderTaboolaGuide } = useReaderTaboolaGuide(route)
const { $toast } = useToastStore()
const userStore = useUserStore()
const { readCompleteTrack, resetReadComplete, chapterVisitTrack, readCompleteTrackDirect } = useChapterReadComplete()
const { elementClick } = useElementClick()
const { start: startReadTime, bookRead } = useBookRead()
const { reportReadChapter } = useFbqReadChapter()
const { reportViewContent } = useFbqViewContent()
const { startSession: startReadDurationSession } = useFbqReadDuration()
let readDurationSession: import('@/composables/fbq/useFbqReadDuration').ReadDurationSession | null = null
const reportedReadChapterIds = new Set<string>()

const { isTrigger: isTriggerWheelOrSwipeUp, listenWheelOrSwipeUp, removeWheelOrSwipeUpListener } = useWheelOrSwipeUpListener()

// 基础状态
const skeleton = ref(true)
const showRecommend = ref(false)
const coinsSuccess = ref(false)
const book: any = ref<GetContentGetcontentdetailResponse['data']>()
const discountPayInfo = ref<Record<string, any>>({
  discountOnceCoins: 0,
  bookImageUrl: '',
})
const showSaveUrl = ref(false)
const showGuidePopup = ref(false)
const containerRef = ref<HTMLElement>()
const chapterRef = ref<HTMLElement>()
const chapter: any = ref<PostReadPlayResponse['data'] | null>()
const content = ref('')
const chapterList = ref<any>([])
const landingList = ref<string[]>([])
const popupTOC = ref(false)
const popupTheme = ref(false)
const showCompleteRecommendLoading = ref(false)

let firstPaidChapterNo: number | null = null
const chapterUnlockRef = ref<any>()

// 加入书架（与书首页共享逻辑）
const { isInShelf, shelfLoading, toggle: onToggleShelf } = useBookshelf(book)

provide('containerRef', containerRef)
provide('chapterConfig', config)

// 使用主题管理 composable (传入品牌默认主题配置)
const { theme, THEME_FONT } = useChapterTheme(config.themeDefaults)
provide('theme', theme)

// 计算属性
const paragraphs = computed(() => {
  if (!content.value)
    return []
  return content.value.split(/[\n]/).filter(Boolean)
})

const { SLOT_COUNT: adSlotCount } = useGptAdManager()
const debug = createDebugger('ad')

const paragraphItems = computed(() => {
  const paras = paragraphs.value
  const threshold = Number(brandConfig.texts?.adSenseCharThreshold) || 500
  if (!features?.enableAdSense || paras.length === 0)
    return paras.map(p => ({ type: 'paragraph' as const, text: p }))

  const items: Array<{ type: 'paragraph'; text: string } | { type: 'ad'; adSlotIndex: number; adInstanceId: string; adOrder: number }> = []
  let charCount = 0
  let adCounter = 0

  for (const para of paras) {
    items.push({ type: 'paragraph', text: para })
    charCount += para.length

    if (charCount >= threshold) {
      items.push({
        type: 'ad',
        adSlotIndex: adCounter % adSlotCount,
        adInstanceId: `page-${props.chapterId}-${adCounter}`,
        adOrder: adCounter + 1,
      })
      adCounter++
      charCount = 0
    }
  }

  return items
})

const adItems = computed(() => paragraphItems.value.filter(item => item.type === 'ad'))

const currentChapter = computed(() => {
  return chapterList.value.find((item: any) => item.id === props.chapterId)
})

watch(
  () => [props.chapterId, content.value],
  () => {
    if (!features?.enableAdSense)
      return
    const sequence = adItems.value.map(item => `#${item.adOrder}:detail${item.adSlotIndex + 1}`).join(', ') || 'none'
    debug('章节广告计划 mode=page chapterId=%s chapterNo=%s threshold=%s adCount=%s sequence=%s',
      props.chapterId, currentChapter.value?.sequenceNo || '', Number(brandConfig.texts?.adSenseCharThreshold) || 500, adItems.value.length, sequence)
  },
  { immediate: true },
)

const isLastChapter = computed(() => {
  return chapterList.value.length > 0 && chapterList.value[chapterList.value.length - 1].id === props.chapterId
})

const isCurrentChapterLocked = computed(() =>
  chapter.value?.lockStatus === CHAPTER_LOCK.LOCK,
)

const showDownloadApp = computed(() => {
  return !!(bannerShow.value && chapter.value)
})

const audioStore = useAudioPlayerStore()
const hasChapterAudio = computed(() => {
  if (audioStore.miniMode && audioStore.contentId === props.contentId)
    return false
  return !!chapter.value?.ttsAudioUrl
})
function onOpenAudioPlayer() {
  if (!chapter.value?.ttsAudioUrl)
    return
  audioStore.openPlayer({
    contentId: props.contentId,
    contentName: book.value?.contentName || '',
    contentCoverUrl: book.value?.contentCoverUrl || '',
    chapterId: props.chapterId,
  })
}

// 滚动相关
const { y, arrivedState } = useScroll(containerRef)
const { reset: resetReadTime } = useInterval(1000, { controls: true })

// 使用滚动管理 composable
const { reportScrollLocation, handleReadComplete } = useChapterScroll({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  chapter,
  chapterList,
  containerRef,
  chapterRef,
  y,
  readCompleteTrack,
})

// 使用埋点上报 composable
const { handleReadTrack, attributionTrack, handleCloseEvent } = useChapterTracking({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  book,
  chapter,
  bookRead,
})

// 错误处理
function onErrorHandle() {
  router.replace({
    name: 'error',
    query: {
      level: ERROR_PAGE_LEVEL.DEFAULT,
      message: 'Network loading failure',
      redirect: window.encodeURIComponent(window.location.href),
    },
  })
}

// 使用章节加载 composable
const { loadChapter, fetchChapter, fetchLatestPosition } = useChapterLoader({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  book,
  chapter,
  content,
  chapterList,
  containerRef,
  skeleton,
  onChapterVisitTrack: chapterVisitTrack,
  onReportScrollLocation: reportScrollLocation,
  onErrorHandle,
})

/**
 * 获取书籍信息
 */
async function fetchBook() {
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>('/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
  firstPaidChapterNo = (data as any).needPayChapterMinNo ?? null
  isInShelf.value = !!data.bookShelf
  // 支付成功且类型是折扣充值则显示成功弹窗
  if (route.query.payment_status === 'success' && Number(route.query?.subscribeType) === VIP_TYPE.DISCOUNT && userStore.$state.landingPageId && userStore.$state.landingPageContentId === props.contentId) {
    coinsSuccess.value = true
    discountPayInfo.value.discountOnceCoins = route.query.discountOnceCoins
    discountPayInfo.value.bookImageUrl = data.contentCoverUrl

    pageViewTrack('sale')
  }
}

/**
 * 获取章节目录
 */
async function fetchChapterList() {
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterList.value = chapterListData.records
    // 同步听书播放器中已解锁章节状态，避免返回阅读页时目录显示锁定
    syncChapterListWithAudioStore(props.contentId, chapterList.value)
  }
  catch (error) {
    onErrorHandle()
  }
}

/**
 * 获取引导语句
 */
async function getLeadSentence() {
  const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(`/content/getLeadSentence?contentId=${props.contentId}`)
  if (!data)
    return
  landingList.value = data?.split('\n')?.filter(Boolean) || []
}

/**
 * 上一章
 */
function onPrevChapter() {
  if (!chapter.value!.prev)
    return

  elementClick('prev_chapter')
  router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.prev.contentChapterId } })
}

/**
 * 下一章
 */
function onNextChapter() {
  // 用户激活上报
  attributionTrack()
  // 完读上报
  if (currentChapter.value.lockStatus !== CHAPTER_LOCK.LOCK)
    readCompleteTrackDirect(props.contentId, props.chapterId, currentChapter.value.sequenceNo, currentChapter.value.chapterVersionId)
  elementClick('next_chapter')

  if (!chapter.value!.next)
    finishRecommend()
  else
    router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.next.contentChapterId } })
}

/**
 * 点击书名 → 跳转书首页
 */
function onClickBook() {
  router.push({ name: 'book', params: { contentId: props.contentId } })
}

/**
 * 打开目录
 */
function onOpenTOC() {
  popupTheme.value = false
  popupTOC.value = true
  attributionTrack()
}

/**
 * 目录检查
 */
function onTOCCheckPass() {
  return true
}

/**
 * 切换主题设置
 */
function onToggleTheme() {
  popupTOC.value = false
  // 锁章页也允许点击切换：用于唤起/收起顶部导航栏；底部设置面板由 themeSettingsVisible 单独门控（锁章时不展示）
  popupTheme.value = !popupTheme.value
}

// 底部设置面板可见性：锁章页强制隐藏，仅非锁章且 popupTheme 时展示
const themeSettingsVisible = computed({
  get: () => popupTheme.value && !isCurrentChapterLocked.value,
  set: (val: boolean) => {
    popupTheme.value = val
  },
})

/**
 * 解锁章节（仅当余额足够时由 ChapterUnlock 的 Unlock 按钮触发；
 * 余额不足时由常驻 PaymentInlinePanel 直接走支付流程，不再经过此函数）
 */
async function onUnlockChapter() {
  if (!chapter.value)
    return

  const autoLock = chapterUnlockRef.value?.autoLock
  if (autoLock !== undefined && autoLock !== userStore.$state.autoLock) {
    await api.post('/member/update/auto-lock', {
      autoLock,
    })
    await userStore.init({ force: true })
  }
  await fetchChapter(resetReadTime)

  if (chapter.value?.lockStatus !== CHAPTER_LOCK.LOCK && !reportedReadChapterIds.has(props.chapterId)) {
    reportReadChapter(props.contentId, props.chapterId, chapter.value?.sequenceNo, firstPaidChapterNo)
    reportedReadChapterIds.add(props.chapterId)
  }
}

/**
 * 支付成功后自动解锁章节
 */
async function handlePaymentSuccess() {
  await userStore.init({ force: true })
  // 仅在章节已就绪且仍锁定时解锁,避免与 loadChapter 内的自动解锁重复请求 /read/play
  if (chapter.value && chapter.value.lockStatus === CHAPTER_LOCK.LOCK) {
    try {
      await fetchChapter(resetReadTime)
    }
    catch {}
  }

  if (chapter.value?.lockStatus !== CHAPTER_LOCK.LOCK && !reportedReadChapterIds.has(props.chapterId)) {
    reportReadChapter(props.contentId, props.chapterId, chapter.value?.sequenceNo, firstPaidChapterNo)
    reportedReadChapterIds.add(props.chapterId)
  }

  const url = new URL(window.location.href)
  url.searchParams.delete('payment_status')
  // 支付成功已解锁,清除 recommend 标记,避免后续 reload 误判展示推荐模块
  url.searchParams.delete('recommend')
  url.searchParams.delete('recommendChapterId')
  history.replaceState({}, '', url.href)
}

/**
 * 完读推荐
 */
function finishRecommend() {
  showCompleteRecommendLoading.value = true
  setTimeout(() => {
    showCompleteRecommendLoading.value = false
    router.push({
      name: 'recommend',
      params: { contentId: props.contentId },
      query: { status: book.value?.finishStatus === 0 ? 'ongoing' : 'finish' },
    })
  }, 800)
}

/**
 * 添加推荐参数
 */
function appendRecommendParams() {
  router.replace({ query: { ...route.query, recommend: '1' } })
}

/**
 * 处理充值成功
 */
function handleCoinsSuccess() {
  coinsSuccess.value = false
  const url = new URL(window.location.href)
  url.searchParams.delete('subscribeType')
  url.searchParams.delete('discountOnceCoins')
  history.replaceState({}, '', url.href)
}

// 拉起 app
const { callApp } = useCallRocnovelApp()

function handleCallApp(contentId: string, sequenceNo: number) {
  callApp(contentId, sequenceNo)
}

// Mounted
onMounted(async () => {
  useGptAdManager().resetGptAdState()
  readDurationSession = startReadDurationSession(props.contentId, props.chapterId)

  // 如果检测到页面是从"往返缓存"中读取的,刷新页面
  window.addEventListener('pageshow', (e) => {
    if (e.persisted)
      window.location.reload()
  })

  // 页面直接关闭时,需上报阅读事件
  window.addEventListener('beforeunload', handleCloseEvent)

  // 只有从支付返回时,显示推荐
  if (route.query.recommend && !route.query.payment_status)
    showRecommend.value = true

  // 展示 Save Url 按钮
  if (route.query.payment_status === 'success' && isInThirdAppBrowser())
    showSaveUrl.value = true

  skeleton.value = true
  try {
    await initPage()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    skeleton.value = false
  }

  useBannerShow()
  listenWheelOrSwipeUp()
})

/**
 * 初始化页面数据（用于 onMounted 与跨书切换时复用）
 */
async function initPage() {
  reportedReadChapterIds.clear()
  getLeadSentence()
  await fetchBook()
  await fetchChapterList()
  reportViewContent(props.contentId, book.value?.contentName, 'chapter', props.chapterId)

  // 支付回跳:跳过"最新阅读位置"跳转,停留在用户付费的当前章节,避免并发导航导致 /500 闪现
  const isPaymentReturn = route.query.payment_status === 'success'
  if (!route.query.skipPosition && !isPaymentReturn) {
    const existed = await fetchLatestPosition()
    if (existed)
      return
  }
  await loadChapter()

  // 基础数据就绪后再处理支付回跳解锁(替代原 immediate watch,消除竞态)
  if (isPaymentReturn)
    await handlePaymentSuccess()

  // 从听书页面跳转过来，定位到音频对应进度
  const audioProgress = Number(route.query.audioProgress)
  if (audioProgress > 0 && containerRef.value) {
    nextTick(() => {
      const scrollHeight = containerRef.value!.scrollHeight - containerRef.value!.clientHeight
      if (scrollHeight > 0) {
        const targetY = (audioProgress / 100) * scrollHeight
        containerRef.value!.scrollTop = targetY
      }
      // 清理 audioProgress 参数，避免刷新后重复定位
      const url = new URL(window.location.href)
      url.searchParams.delete('audioProgress')
      history.replaceState({}, '', url.href)
    })
  }

  if (chapter.value?.lockStatus !== CHAPTER_LOCK.LOCK && !reportedReadChapterIds.has(props.chapterId)) {
    reportReadChapter(props.contentId, props.chapterId, chapter.value?.sequenceNo, firstPaidChapterNo)
    reportedReadChapterIds.add(props.chapterId)
  }
  startReadTime.value = new Date().getTime()
}

/**
 * 同书章节切换
 */
async function handleChapterChange(newChapterId: string) {
  useGptAdManager().resetGptAdState()
  handleReadTrack()
  skeleton.value = true
  chapter.value = null
  content.value = ''
  await loadChapter()
  resetReadComplete()
  if (chapter.value?.lockStatus !== CHAPTER_LOCK.LOCK && !reportedReadChapterIds.has(newChapterId)) {
    reportReadChapter(props.contentId, newChapterId, chapter.value?.sequenceNo, firstPaidChapterNo)
    reportedReadChapterIds.add(newChapterId)
  }
}

// Watches
watch([() => props.contentId, () => props.chapterId], async ([newContentId, newChapterId], [oldContentId, oldChapterId]) => {
  if (!oldContentId || !oldChapterId)
    return
  if (newContentId !== oldContentId) {
    // 跨书切换：结束旧阅读时长会话并重新初始化页面
    useGptAdManager().resetGptAdState()
    readDurationSession?.endSession()
    readDurationSession = startReadDurationSession(newContentId, newChapterId)
    skeleton.value = true
    try {
      await initPage()
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }
    finally {
      skeleton.value = false
    }
  }
  else if (newChapterId !== oldChapterId) {
    // 同书章节切换
    await handleChapterChange(newChapterId)
  }
})

watch(() => route.fullPath, () => {
  popupTOC.value = false
  popupTheme.value = false
})

watch(() => route.query.recommend, (val) => {
  if (val && !route.query.payment_status)
    showRecommend.value = true
  else if (!val)
    showRecommend.value = false
})

// 支付回跳自动解锁在 onMounted 基础数据就绪后串行处理(见 onMounted),不再用 immediate watch,避免竞态导致 /500 闪现

watch(() => isTriggerWheelOrSwipeUp.value, (val) => {
  if (val && !popupTOC.value && isLastChapter.value) {
    setTimeout(() => {
      if (y.value === 0 || (y.value > 0 && arrivedState.bottom))
        finishRecommend()
    }, 100)
  }
})

watchThrottled(y, async () => {
  reportScrollLocation(y.value)
  handleReadComplete()
}, { throttle: 2000, leading: true })

// Unmounted
onUnmounted(() => {
  readDurationSession?.endSession()
  handleReadTrack()
  window.removeEventListener('beforeunload', handleCloseEvent)
  removeWheelOrSwipeUpListener()
})
</script>

<template>
  <div :class="`brand-${brand}`">
    <!-- 骨架屏 -->
    <div v-if="skeleton" class="min-h-100dvh" :style="`background: ${theme.background}`">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
    </div>

    <!-- 主容器 -->
    <div v-else>
      <div ref="containerRef" :class="`h-100dvh overflow-scroll color-${theme.background.replace('#', '')}`" :style="`background: ${theme.background}`">
        <!-- 下载 App Banner -->
        <BizDownloadApp
          v-if="showDownloadApp"
          :id="contentId"
          position="fixed"
          class="fixed top-0 w-full"
          :index="chapter?.sequenceNo"
          from="chapter"
        />

        <!-- 导航栏（点击屏幕唤起/收起，与底部设置面板联动） -->
        <transition name="rr-topbar">
          <div
            v-show="popupTheme"
            class="fixed w-full z-50"
            :class="{
              'top-60px': bannerShow,
              'top-0': !bannerShow,
            }"
            style="transform: translate(0)"
          >
            <ChapterBreadcrumb
              :book-name="book?.contentName"
              :chapter-name="chapter?.contentChapterName"
              :is-in-shelf="isInShelf"
              :shelf-loading="shelfLoading"
              @click-book="onClickBook"
              @toggle-shelf="onToggleShelf"
            />

            <!-- Save URL 按钮 -->
            <div class="absolute right-12px top-4px z-99 flex items-end">
              <div v-if="showSaveUrl" class="theme-save-btn w-80px h-28px flex items-center justify-center text-12px font-medium rounded-14px" @click="showGuidePopup = true">
                Save Url
              </div>
            </div>
          </div>
        </transition>

        <!-- 悬浮音频按钮 -->
        <div
          v-if="hasChapterAudio"
          class="fixed right-16px z-40 flex items-center justify-center w-44px h-44px rounded-full shadow-lg cursor-pointer"
          :class="{ 'opacity-50': !themeSettingsVisible && !popupTOC }"
          style="top: 50%; transform: translateY(-50%); background: var(--primary-color, #9875FB)"
          @click.stop="onOpenAudioPlayer"
        >
          <i-icon-park-outline-headset class="text-20px text-white park-icon" />
        </div>

        <!-- 章节内容 -->
        <div
          v-if="chapter"
          ref="chapterRef"
          class="px-20px pb-10 min-h-100dvh"
          :class="{
            'pt-24': bannerShow,
            'pt-6': !bannerShow,
          }"
          @click="onToggleTheme"
        >
          <!-- 引导语 -->
          <ChapterLeadSentence
            :landing-list="landingList"
            :sequence-no="chapter?.sequenceNo"
            :lead-sentence-style="THEME_FONT.leadSentence"
          />

          <!-- 章节标题 -->
          <p :style="THEME_FONT.chapterTitle" class="chapter-title font-800">
            {{ chapter?.contentChapterName }}
          </p>

          <!-- 章节正文 -->
          <div :style="THEME_FONT.chapterContent" class="chapter-content">
            <template v-for="(item, index) in paragraphItems">
              <p v-if="item.type === 'paragraph'" :key="`p-${index}`" :style="THEME_FONT.paragraphSpacing">
                {{ item.text }}
              </p>
              <ScrollChapterAdGpt
                v-else
                :key="item.adInstanceId"
                :ad-slot-index="item.adSlotIndex"
                :ad-instance-id="item.adInstanceId"
                :ad-order="item.adOrder"
                :chapter-id="chapterId"
              />
            </template>
          </div>

          <!-- 解锁支付模块 -->
          <ChapterUnlock
            ref="chapterUnlockRef"
            :chapter="chapter"
            :content-id="contentId"
            :content-chapter-id="chapterId"
            :book-name="book?.contentName"
            :serial-status="book?.finishStatus"
            :force-show-panel="showRecommend"
            @pay-success="appendRecommendParams"
          />

          <!-- 导航按钮（recommend 模式下隐藏所有按钮） -->
          <ChapterNavigation
            v-if="!showRecommend && !isCurrentChapterLocked"
            :chapter="chapter"
            :show-recommend="showRecommend"
            @prev-chapter="onPrevChapter"
            @next-chapter="onNextChapter"
            @open-toc="onOpenTOC"
            @unlock-chapter="onUnlockChapter"
          />

          <!-- 推荐模块 -->
          <ChapterRecommend
            v-if="showRecommend"
            class="mt-24px"
            :book-id="contentId"
            @read="appendRecommendParams"
            @collection="appendRecommendParams"
            @error="showRecommend = false"
          />

          <!-- 在 App 中继续阅读按钮（仅在支付返回 + 显示推荐时出现） -->
          <div v-if="showRecommend && showDownloadApp" class="mt-24px text-center">
            <a
              class="theme-sub-btn inline-block px-24px lh-1em py-3 text-center min-h-40px text-15px text-white text-center rounded-full w-300px font-medium"
              @click="handleCallApp(contentId, chapter?.sequenceNo)"
            >
              {{ config.texts.continueInApp }}
            </a>
          </div>
        </div>

        <!-- 主题设置弹窗 -->
        <ChapterThemeSettings
          v-model="themeSettingsVisible"
          :chapter="chapter"
          @prev-chapter="onPrevChapter"
          @next-chapter="onNextChapter"
          @open-toc="onOpenTOC"
        />

        <!-- 目录弹窗 -->
        <ChapterTOCPopup
          v-model="popupTOC"
          :book="book"
          :content-id="props.contentId"
          :chapter-id="props.chapterId"
          :check-pass-goto-chapter="() => onTOCCheckPass()"
          :style="{ '--toc-border-color': '#F6F7F9' }"
        />

        <!-- 充值成功弹窗 -->
        <ChapterCoinsSuccess v-model="coinsSuccess" :discount-pay-info="discountPayInfo" @close="handleCoinsSuccess" />

        <!-- 引导弹窗 -->
        <WelfareGuideOpen v-model="showGuidePopup" />

        <ReaderTaboolaGuide v-model="showReaderTaboolaGuide" />

        <!-- 完读加载中 -->
        <div v-if="showCompleteRecommendLoading" class="fixed top-0 left-0 flex justify-center items-center w-full h-full">
          <BasicSpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// 引入主题样式(不使用 scoped,让子组件也能使用这些全局主题类)
@import './styles/chapter-theme.scss';

// talewave 品牌隐藏导语引号图标
.brand-talewave .bg-icon {
  display: none;
}
</style>

<style lang="scss" scoped>
.slide-enter-active {
  transition: all 0.2s ease;
  transform: translateY(0%);
}

.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-100%);
}

/* 顶部导航栏唤起/收起动效 */
.rr-topbar-enter-active,
.rr-topbar-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.rr-topbar-enter,
.rr-topbar-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
